/**
 * The "Approve call from Ellie?" sheet. Mounted once, globally (in
 * RootNavigator). When a `voice_call_approval` push is tapped, the push handler
 * calls showVoiceApproval() and this presents. Approve/Deny call
 * POST /realtime/approve-call/{account}/{cognito} — the backend promotes the live
 * call to the member's full session (approve) or rejects it (deny).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Pressable, View, Text, ActivityIndicator } from 'react-native';
import { useTheme } from '../hooks/useTheme';
import { useSheetLayout } from '../utilities/sheetLayout';
import { apiPost } from '../utilities/apiClient';
import { getSessionIds } from '../store/actions/_sessionIds';
import debug from '../utilities/debug';
import {
  PendingVoiceApproval,
  subscribeVoiceApproval,
  clearVoiceApproval,
} from '../utilities/voiceApprovalStore';

export default function VoiceCallApprovalSheet(): JSX.Element {
  const theme = useTheme();
  const sheet = useSheetLayout({ desktopMaxWidth: 460 });
  const [pending, setPending] = useState<PendingVoiceApproval | null>(null);
  const [busy, setBusy] = useState<null | 'approve' | 'deny'>(null);

  useEffect(() => subscribeVoiceApproval(setPending), []);

  const act = useCallback(
    async (decision: 'approve' | 'deny') => {
      if (!pending || busy) return;
      setBusy(decision);
      try {
        const { accountId, cognitoId } = getSessionIds();
        await apiPost(`/realtime/approve-call/${accountId}/${cognitoId}`, {
          call_id: pending.callId,
          decision,
        });
      } catch (err: any) {
        debug.warn('[voice-approval] action failed', { decision, err: err?.message });
      } finally {
        setBusy(null);
        clearVoiceApproval();
      }
    },
    [pending, busy],
  );

  const dismiss = useCallback(() => {
    if (!busy) clearVoiceApproval();
  }, [busy]);

  return (
    <Modal
      visible={!!pending}
      transparent
      animationType={sheet.animationType}
      onRequestClose={dismiss}
    >
      <Pressable
        style={[{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'flex-end' }, sheet.overlayStyle]}
        onPress={dismiss}
      >
        <Pressable
          style={[
            {
              backgroundColor: theme.colors.card,
              borderTopLeftRadius: theme.radius.lg,
              borderTopRightRadius: theme.radius.lg,
              paddingHorizontal: theme.spacing.lg,
              paddingBottom: theme.spacing.xl,
            },
            sheet.sheetStyle,
          ]}
          onPress={() => {}}
        >
          {sheet.showHandle && (
            <View
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.colors.border,
                alignSelf: 'center',
                marginTop: theme.spacing.sm,
                marginBottom: theme.spacing.md,
              }}
            />
          )}

          <Text style={{ fontSize: theme.font.size.xl, fontWeight: theme.font.weight.bold, color: theme.colors.text, marginBottom: theme.spacing.xs }}>
            Approve call from Ellie?
          </Text>
          <Text style={{ fontSize: theme.font.size.md, lineHeight: 22, color: theme.colors.muted, marginBottom: theme.spacing.lg }}>
            {pending?.planName ? `Calling about ${pending.planName}. ` : 'Verify this phone call to continue. '}
            {pending?.phone ? `From ${pending.phone}.` : ''}
          </Text>

          <Pressable
            testID="voice-approve"
            accessibilityRole="button"
            disabled={!!busy}
            onPress={() => act('approve')}
            style={{
              backgroundColor: theme.colors.primary,
              borderRadius: theme.radius.md,
              paddingVertical: theme.spacing.md,
              alignItems: 'center',
              opacity: busy ? 0.7 : 1,
              marginBottom: theme.spacing.sm,
            }}
          >
            {busy === 'approve' ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={{ color: '#fff', fontSize: theme.font.size.md, fontWeight: theme.font.weight.bold }}>Approve</Text>
            )}
          </Pressable>

          <Pressable
            testID="voice-deny"
            accessibilityRole="button"
            disabled={!!busy}
            onPress={() => act('deny')}
            style={{ paddingVertical: theme.spacing.md, alignItems: 'center', opacity: busy ? 0.7 : 1 }}
          >
            <Text style={{ color: theme.colors.muted, fontSize: theme.font.size.md }}>
              {busy === 'deny' ? 'Declining…' : 'Deny'}
            </Text>
          </Pressable>
        </Pressable>
      </Pressable>
    </Modal>
  );
}
